import { MouseEvent, useEffect, useMemo } from 'react';
import { DateInput, Labeled, NumberInput, ReferenceField, SelectInput, TextInput, TextField, useRecordContext, useGetOne, useGetMany, NullableBooleanInput } from 'react-admin';
import { ToggleButton, ToggleButtonGroup, Alert, Typography, Box, MenuItem } from '@mui/material';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { RichTextInput } from 'ra-input-rich-text';

import { PriceField } from '../components/PriceField';
import { LinesInput } from './lines/LinesInput';
import { ContractTransportPanel } from './ContractTransportPanel';
import { SubTotalField } from './SubTotalField';
import { ClientSelectList } from '../payments/ClientSelectList';
import { Contract } from '../types';
import {
  AT_NEED_DEFAULT_LINES,
  PRE_NEED_DEFAULT_LINES,
  ONTARIO_AT_NEED_DEFAULT_LINES,
  cloneDefaultLines,
  linesMatchDefaults,
} from 'paperman-contract-defaults';

export const ContractBaseEdit = () => {
  const default_issue_date = useMemo(
    () => new Date().toISOString().split('T')[0],
    []
  );
  const record = useRecordContext<Contract>();

  if(record == null) return null;

  return (
    <>
    <ContractTypeToggle />
    <PreneedPaymentOptions />
    <AffirmOption />
    <ReferenceField
      source="funeral_id"
      reference="funerals"
      link={false}
    >
      <ReferenceField source="deceased_client_id" reference="clients">
          <Labeled>
            <TextField source="name" label="Deceased" />
          </Labeled>
        </ReferenceField>
      </ReferenceField>

      {/* <ReferenceField source="client_id" reference="clients">
        <SelectInput source="client_id" />
        </ReferenceField> */}
      <ClientSelectList contract={record} />

      <ContractTransportPanel />


        {/* <ReferenceField
      source="funeral_id"
      reference="funerals"
      link={false}
    >
        <ReferenceField source="primary_client_id" reference="clients">
          <Labeled>
            <TextField source="name" label="Primary Client" />
          </Labeled>
        </ReferenceField>
      </ReferenceField> */}
      {/* <BooleanInput source="is_preneed" label="Pre-Need Contract"/> */}
      <DateInput
        source="issue_date"
        defaultValue={default_issue_date}
        parse={(value) => new Date(value).toISOString().split('T')[0]}
      />
      <LinesInput source="lines" fullWidth />
      <SubTotalField source="subtotal" />
      <Labeled>
        <PriceField source="total_amount" />
      </Labeled>
      <RichTextInput source="memo" label="Special Instructions" fullWidth />

      {/* <TextInput
        source="signed_contract"
        parse={(value: string) => {
          if (value && !value.startsWith('http')) {
            return `https://${value}`;
          }
          return value;
        }}
      /> */}
    </>
  );
};

const ContractTypeToggle = () => {
  const record = useRecordContext<Contract>();
  const { control, getValues, setValue } = useFormContext();
  const {
    field: { value, onChange, onBlur },
  } = useController({
    name: 'is_preneed',
    control,
    defaultValue: false,
  });

  // Fetch funeral data to get the region
  const { data: funeral } = useGetOne(
    'funerals',
    { 
      id: record?.funeral_id,
      meta: {
        select: 'id, region'
      }
    },
    { enabled: !!record?.funeral_id }
  );

  const normalizedValue = value === undefined || value === null ? false : !!value;

  const handleChange = (_event: MouseEvent<HTMLElement>, newValue: 'at' | 'pre' | null) => {
    if (newValue === null) {
      return;
    }

    const newIsPreneed = newValue === 'pre';
    const previousIsPreneed = normalizedValue;
    const currentLines = getValues('lines');

    const shouldReplaceLines = () => {
      if (!Array.isArray(currentLines) || currentLines.length === 0) {
        return true;
      }

      const isOntario = funeral?.region?.toLowerCase() === 'ontario';

      if (previousIsPreneed && !newIsPreneed) {
        // Switching from preneed to at-need: check if current lines match preneed defaults
        return linesMatchDefaults(currentLines, PRE_NEED_DEFAULT_LINES);
      }

      if (!previousIsPreneed && newIsPreneed) {
        // Switching from at-need to preneed: check if current lines match at-need defaults
        // For Ontario contracts, check Ontario defaults; otherwise check Quebec defaults
        if (isOntario) {
          return linesMatchDefaults(currentLines, ONTARIO_AT_NEED_DEFAULT_LINES);
        }
        return linesMatchDefaults(currentLines, AT_NEED_DEFAULT_LINES);
      }

      return false;
    };

    if (shouldReplaceLines()) {
      setValue('lines', cloneDefaultLines(newIsPreneed ? 'pre' : 'at', funeral?.region), {
        shouldDirty: true,
        shouldTouch: true,
      });
    }

    if (!newIsPreneed) {
      if (normalizedValue) {
        setValue('is_monthly', false, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
      const monthlyAmount = getValues('monthly_payment_amount');
      if (monthlyAmount !== null && monthlyAmount !== undefined && monthlyAmount !== '') {
        setValue('monthly_payment_amount', null, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    }

    onChange(newIsPreneed);
  };

  return (
    <Labeled label="Contract Type">
      <ToggleButtonGroup
        exclusive
        value={normalizedValue ? 'pre' : 'at'}
        onChange={handleChange}
        onBlur={onBlur}
        size="small"
      >
        <ToggleButton value="at">At-Need</ToggleButton>
        <ToggleButton value="pre">Pre-Need</ToggleButton>
      </ToggleButtonGroup>
    </Labeled>
  );
};

const AffirmOption = () => {
  const { control } = useFormContext();
  const isPreneed = !!useWatch({ name: 'is_preneed', control });

  if (isPreneed) return null;

  return (
    <NullableBooleanInput
      source="affirm"
      label="Enable Affirm Payment Option"
      helperText="Allow customers to pay with Affirm at checkout"
    />
  );
};

const PreneedPaymentOptions = () => {
  const { control, setValue } = useFormContext();
  const isPreneed = !!useWatch({ name: 'is_preneed', control });
  const {
    field: { value: isMonthly, onChange: onMonthlyChange, onBlur },
  } = useController({
    name: 'is_monthly',
    control,
    defaultValue: false,
  });

  const monthlyPaymentAmount = useWatch({
    name: 'monthly_payment_amount',
    control,
  });

  useEffect(() => {
    if (!isPreneed) {
      if (isMonthly) {
        setValue('is_monthly', false, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }

      if (monthlyPaymentAmount !== null && monthlyPaymentAmount !== undefined && monthlyPaymentAmount !== '') {
        setValue('monthly_payment_amount', null, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    }
  }, [isPreneed, isMonthly, monthlyPaymentAmount, setValue]);

  if (!isPreneed) {
    return null;
  }

  const handlePaymentModeChange = (_event: MouseEvent<HTMLElement>, nextValue: 'full' | 'monthly' | null) => {
    if (nextValue === null) {
      return;
    }

    const nextIsMonthly = nextValue === 'monthly';

    if (!nextIsMonthly) {
      setValue('monthly_payment_amount', null, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }

    onMonthlyChange(nextIsMonthly);
  };

  const showMonthlyAmount = !!isMonthly;

  return (
    <>
      <Labeled label="Pre-Need Payment Schedule">
        <ToggleButtonGroup
          exclusive
          value={showMonthlyAmount ? 'monthly' : 'full'}
          onChange={handlePaymentModeChange}
          onBlur={onBlur}
          size="small"
        >
          <ToggleButton value="full">Paid in Full</ToggleButton>
          <ToggleButton value="monthly">Monthly</ToggleButton>
        </ToggleButtonGroup>
      </Labeled>
      {showMonthlyAmount && (
        <>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <SelectInput
              source="payment_term"
              label="Payment Term"
              defaultValue={36}
              choices={[
                { id: 12, name: '12 months (1 year)' },
                { id: 36, name: '36 months (3 years)' },
              ]}
              sx={{ minWidth: 200 }}
            />
            <NumberInput
              source="down_payment_amount"
              label="Down Payment Amount"
              helperText="Minimum $400.00"
              min={400}
              step={0.01}
              sx={{ minWidth: 200 }}
              parse={(value) => {
                if (!value || value === '') return null;
                const num = typeof value === 'string' ? parseFloat(value) : value;
                return isNaN(num) ? null : num;
              }}
              format={(value) => {
                if (!value && value !== 0) return '';
                return typeof value === 'number' ? value.toFixed(2) : value;
              }}
              validate={(value) => {
                if (showMonthlyAmount && value !== null && value !== undefined && value !== '') {
                  const numValue = typeof value === 'string' ? parseFloat(value) : value;
                  if (isNaN(numValue) || numValue < 400) {
                    return 'Down payment must be at least $400.00';
                  }
                }
                return undefined;
              }}
            />
          </Box>
          <MonthlyPaymentInfo />
        </>
      )}
    </>
  );
};

const MonthlyPaymentInfo = () => {
  const record = useRecordContext();
  const { watch } = useFormContext();
  
  // Watch for down payment amount, total amount, and payment term changes
  const downPaymentAmount = watch('down_payment_amount');
  const totalAmount = watch('total_amount');
  const paymentTerm = watch('payment_term') || 36;

  // Get the funeral data with deceased client information
  const { data: funeral, isLoading: funeralLoading } = useGetOne(
    'funerals',
    { 
      id: record?.funeral_id,
      meta: {
        select: 'id, region, deceased_client_id, date_time_of_death, deceased_client:deceased_client_id(*)'
      }
    },
    { enabled: !!record?.funeral_id }
  );

  // If funeral doesn't have deceased_client populated, fetch it separately
  const { data: deceasedClients, isLoading: clientLoading } = useGetMany(
    'clients',
    { ids: funeral?.deceased_client_id ? [funeral.deceased_client_id] : [] },
    { enabled: !!funeral?.deceased_client_id && !funeral?.deceased_client }
  );

  const isLoading = funeralLoading || clientLoading;

  // Get the deceased client from either the funeral object or the separate fetch
  const deceasedClient = funeral?.deceased_client || deceasedClients?.[0];

  // 3-year (36-month) rates
  const getMonthlyPaymentRate36 = (age: number): number => {
    if (age <= 50) return 0.0280;
    if (age <= 55) return 0.0290;
    if (age <= 60) return 0.0315;
    if (age <= 65) return 0.0340;
    if (age <= 70) return 0.0350;
    if (age <= 75) return 0.0370;
    if (age <= 80) return 0.0390;
    if (age <= 85) return 0.0410;
    return 0.0410; // 86+ (N/A for 3-year)
  };

  // 1-year (12-month) rates
  const getMonthlyPaymentRate12 = (age: number): number => {
    if (age <= 50) return 0.0835;
    if (age <= 55) return 0.0835;
    if (age <= 60) return 0.0835;
    if (age <= 65) return 0.0840;
    if (age <= 70) return 0.0840;
    if (age <= 75) return 0.0845;
    if (age <= 80) return 0.0850;
    if (age <= 85) return 0.0855;
    return 0.0860;
  };

  const getMonthlyPaymentRate = (age: number, term: number): number => {
    return term === 12 ? getMonthlyPaymentRate12(age) : getMonthlyPaymentRate36(age);
  };

  // Ontario TruStage 0088-911 rates. Mirror of the backend source of truth
  // (packages/nestjs/src/trustage/trustage-rates.ontario.ts) — keep in sync.
  // 1-Year Pay (FEGA IP) is flat; 3-Year Time Pay (FEIA, Funeral Value) is per-age
  // and N/A above age 82. Returns null when the age is out of range for the term.
  const ontarioThreeYearFvBreakpoints: [number, number][] = [
    [0, 0.02969], [59, 0.0298], [60, 0.0299], [61, 0.03001], [62, 0.03011],
    [63, 0.03022], [64, 0.03053], [65, 0.03085], [66, 0.03116], [67, 0.03148],
    [68, 0.03179], [69, 0.03206], [70, 0.03232], [71, 0.03258], [72, 0.03284],
    [73, 0.03311], [74, 0.03333], [75, 0.03355], [76, 0.03377], [77, 0.03399],
    [78, 0.03421], [79, 0.03443], [80, 0.03465], [81, 0.03487], [82, 0.03509],
  ];
  const getOntarioRate = (age: number, term: number): number | null => {
    if (term === 12) return 0.08334; // 1-Year Pay (FEGA IP), flat all ages
    if (age < 0 || age > 82) return null; // 3-Year N/A above age 82
    let r = ontarioThreeYearFvBreakpoints[0][1];
    for (const [startAge, factor] of ontarioThreeYearFvBreakpoints) {
      if (age >= startAge) r = factor;
      else break;
    }
    return r;
  };

  // Function to get age bracket description
  const getAgeBracket = (age: number): string => {
    if (age <= 50) return '0-50';
    if (age <= 55) return '51-55';
    if (age <= 60) return '56-60';
    if (age <= 65) return '61-65';
    if (age <= 70) return '66-70';
    if (age <= 75) return '71-75';
    if (age <= 80) return '76-80';
    if (age <= 85) return '81-85';
    return '86+';
  };

  if (isLoading) {
    return (
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Loading client information...
        </Typography>
      </Box>
    );
  }

  if (!funeral) {
    return (
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
        <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
          <strong>Monthly Payment Calculation</strong>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Monthly payment rate is based on deceased client's age. Please ensure the contract is linked to a funeral.
        </Typography>
      </Box>
    );
  }

  if (!deceasedClient) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Warning:</strong> No deceased client found. Please ensure the funeral has a deceased client assigned to calculate the correct monthly payment rate.
        </Typography>
      </Alert>
    );
  }

  if (!deceasedClient.birthdate) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Warning:</strong> Deceased client <strong>{deceasedClient.name}</strong> does not have a birthdate set. 
          Please add the birthdate to determine the correct monthly payment rate.
        </Typography>
      </Alert>
    );
  }

  // Calculate age
  const birthDate = new Date(deceasedClient.birthdate);
  const deathDate = funeral.date_time_of_death 
    ? new Date(funeral.date_time_of_death)
    : new Date(); // Use current date if no death date
    
  let age = deathDate.getFullYear() - birthDate.getFullYear();
  const monthDiff = deathDate.getMonth() - birthDate.getMonth();
  
  // Adjust age if birthday hasn't occurred yet this year
  if (monthDiff < 0 || (monthDiff === 0 && deathDate.getDate() < birthDate.getDate())) {
    age--;
  }

  const isOntario = funeral?.region?.toLowerCase() === 'ontario';
  const rate = isOntario
    ? getOntarioRate(age, paymentTerm)
    : getMonthlyPaymentRate(age, paymentTerm);

  // Ontario 3-Year Time Pay has no rate above age 82 (rate is null there).
  const ontarioOutOfRange = isOntario && rate === null;
  const ageBracket = isOntario ? null : getAgeBracket(age);
  const ratePercent = rate != null ? (rate * 100).toFixed(2) : null;

  // Quebec-only advisory (86+). Ontario handles its own cap via ontarioOutOfRange.
  const isAge86Plus = !isOntario && age >= 86;
  const isAge86PlusOn3Year = isAge86Plus && paymentTerm === 36;

  return (
    <>
      {ontarioOutOfRange && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Not available:</strong> The Ontario 3-year (36-month) plan is not offered
            above age 82 (client is {age}). Select the 1-year term or Paid in Full.
          </Typography>
        </Alert>
      )}
      {isAge86PlusOn3Year && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Warning:</strong> Client is {age} years old (86+). The 3-year payment plan is not available for this age group. Please select the 1-year (12-month) term.
          </Typography>
        </Alert>
      )}
      {isAge86Plus && !isAge86PlusOn3Year && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Warning:</strong> Client is {age} years old (86+). Please review this monthly payment arrangement carefully.
          </Typography>
        </Alert>
      )}
      {!ontarioOutOfRange && (
        <Box sx={{
          mt: 1,
          p: 1.5,
          maxWidth: 480,
          backgroundColor: isAge86Plus ? '#fff3e0' : '#f5f5f5',
          borderRadius: 1,
          borderLeft: `3px solid ${isAge86Plus ? '#ff9800' : '#2196f3'}`
        }}>
          <Typography variant="body2" color={isAge86Plus ? 'warning.main' : 'primary'} sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Monthly Payment Calculation
          </Typography>
          <Typography variant="body2">
            {deceasedClient.name} (Age: {age}){ageBracket ? ` — Bracket: ${ageBracket}` : ''} — Rate: {ratePercent}%
          </Typography>
          {downPaymentAmount && downPaymentAmount > 0 && (
            <Typography variant="body2">
              Down Payment: ${downPaymentAmount.toFixed(2)}
            </Typography>
          )}
          {totalAmount ? (
            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 'bold' }}>
              Estimated Monthly Payment: ${((((totalAmount / 100) - (downPaymentAmount || 0)) * (rate as number))).toFixed(2)}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Formula: {downPaymentAmount && downPaymentAmount > 0
                ? `(Contract Total - Down Payment) × ${ratePercent}%`
                : `Contract Total × ${ratePercent}%`}
            </Typography>
          )}
        </Box>
      )}
    </>
  );
};
